import { useContext, useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { getTagsItemOptionsList } from '@aytm/app/stats/page/comments/lib/utils'

import SettingsButton from '@aytm/app/stats/page/sidebar/coding/components/SettingsButton'
import { countReportsUsingTag } from '@aytm/app/stats/page/sidebar/coding/lib/used-reports'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

const TagSettings = ({
  tagRef,
  tag,
  opened = false,
  isEditing = false,
  isProcessing = false,
  isCurrentProcessing = false,
  onConfirm
}) => {
  const { mode, reports, reportId, onAction, onCancelEditing, onToggleSettings } = useContext(DataContext)

  const [selectedAction, setSelectedAction] = useState(null)
  const [usedReportsCount, setUsedReportsCount] = useState(0)

  const onActionCancel = useCallback(() => {
    setSelectedAction(null)
    if (isEditing) onCancelEditing()
  }, [isEditing, onCancelEditing])

  const onActionConfirm = useCallback(() => {
    onConfirm(selectedAction)
  }, [selectedAction, onConfirm])

  const onActionClick = useCallback(
    action => {
      if ('assign' != action) setSelectedAction(action)
      if (onAction) onAction(action, tag)
    },
    [tag, onAction]
  )

  const onToggle = useCallback(() => onToggleSettings(tag.tag, tag.type, tag.codebook || null), [tag, onToggleSettings])

  const options = useMemo(
    () =>
      getTagsItemOptionsList({
        specialMode: mode,
        tag,
        selectedAction,
        onActionCancel,
        onActionConfirm,
        onActionClick
      }),
    [mode, tag, selectedAction, onActionCancel, onActionConfirm, onActionClick]
  )

  useEffect(() => {
    if (['update', 'delete'].includes(selectedAction)) {
      setUsedReportsCount(countReportsUsingTag(reports, tag.tag))
    } else setUsedReportsCount(0)
  }, [tag.tag, reportId, reports, selectedAction])

  return 0 < options.length ? (
    <SettingsButton
      itemRef={tagRef}
      options={options}
      warning={
        selectedAction && ['update', 'delete'].includes(selectedAction) && 0 < usedReportsCount
          ? `This code is used in ${usedReportsCount} ${pluralize(usedReportsCount, 'report', 'reports')}. They will also be affected.`
          : null
      }
      opened={opened}
      isEditing={isEditing}
      isProcessing={isProcessing}
      isCurrentProcessing={isCurrentProcessing}
      onActionCancel={onActionCancel}
      onToggle={onToggle}
    />
  ) : (
    <div className={style['component-settings']}></div>
  )
}

TagSettings.propTypes = {
  tagRef: PropTypes.any,
  tag: PropTypes.object,
  opened: PropTypes.bool,
  isEditing: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  onConfirm: PropTypes.func
}

TagSettings.displayName = 'TagSettings'

export default TagSettings
